import type { AuthModalMode } from './modalAdapter.js'

export type ModalFlowKind = 'auth-login' | 'subscription-control' | 'sync-activation'
export type AuthLoginStep =
  | 'email'
  | 'checking-human'
  | 'checking-human-failed'
  | 'email-sent'
  | 'username'
  | 'done'
export type SubscriptionControlStep =
  | 'cancel-confirm'
  | 'cancel-success'
  | 'reactivate-confirm'
  | 'reactivate-success'
export type SyncActivationStep =
  | 'threshold'
  | 'options'
  | 'activating'
  | 'login-ready'
  | 'landing'
  | 'timeout'
  | 'reactivate-from-deactivated'
  | 'username'
  | 'done'

export type ModalFlowState =
  | { flow: 'none', revision: number }
  | {
    flow: 'auth-login'
    step: AuthLoginStep
    owner: 'auth-login'
    revision: number
  }
  | {
    flow: 'subscription-control'
    step: SubscriptionControlStep
    owner: 'subscription-control'
    revision: number
  }
  | {
    flow: 'sync-activation'
    step: SyncActivationStep
    owner: 'sync-activation'
    revision: number
  }

export type ModalFlowEvent =
  | { type: 'START_AUTH_LOGIN', step?: AuthLoginStep }
  | { type: 'START_SUBSCRIPTION_CANCEL' }
  | { type: 'START_SUBSCRIPTION_REACTIVATE' }
  | { type: 'START_SYNC_ACTIVATION', step?: SyncActivationStep }
  | { type: 'START_DEACTIVATED_SYNC_REACTIVATION' }
  | { type: 'AUTH_EMAIL_SUBMITTED' }
  | { type: 'AUTH_HUMAN_CHECK_REQUIRED' }
  | { type: 'AUTH_HUMAN_CHECK_FAILED' }
  | { type: 'AUTH_OTP_SENT' }
  | { type: 'AUTH_CHANGE_EMAIL' }
  | { type: 'AUTH_OTP_VERIFIED' }
  | { type: 'AUTH_PROFILE_MISSING_USERNAME' }
  | { type: 'AUTH_USERNAME_SAVED' }
  | { type: 'SUBSCRIPTION_CANCEL_CONFIRMED' }
  | { type: 'SUBSCRIPTION_CANCEL_OK' }
  | { type: 'SUBSCRIPTION_CANCEL_FAIL' }
  | { type: 'SUBSCRIPTION_REACTIVATE_CONFIRMED' }
  | { type: 'SUBSCRIPTION_REACTIVATE_OK' }
  | { type: 'SUBSCRIPTION_REACTIVATE_FAIL' }
  | { type: 'SYNC_ACTIVATION_OPTIONS' }
  | { type: 'SYNC_ACTIVATION_CHECKOUT_RETURN' }
  | { type: 'SYNC_ACTIVATION_READY_FOR_FRESH_LOGIN' }
  | { type: 'SYNC_ACTIVATION_VERIFICATION_FAILED' }
  | { type: 'SYNC_ACTIVATION_PROFILE_ACTIVE' }
  | { type: 'SYNC_ACTIVATION_TIMEOUT' }
  | { type: 'SYNC_ACTIVATION_RETRY' }
  | { type: 'SYNC_ACTIVATION_MISSING_USERNAME' }
  | { type: 'SYNC_ACTIVATION_USERNAME_SAVED' }
  | { type: 'COMPLETE' }
  | { type: 'CANCEL' }

export const EMPTY_MODAL_FLOW_STATE: ModalFlowState = {
  flow: 'none',
  revision: 0
}

export function reduceModalFlowEvent(
  state: ModalFlowState,
  event: ModalFlowEvent
): ModalFlowState {
  if (event.type === 'START_AUTH_LOGIN') {
    return createAuthLoginState(event.step ?? 'email', state.revision + 1)
  }

  if (event.type === 'START_SUBSCRIPTION_CANCEL') {
    return createSubscriptionControlState('cancel-confirm', state.revision + 1)
  }

  if (event.type === 'START_SUBSCRIPTION_REACTIVATE') {
    return createSubscriptionControlState('reactivate-confirm', state.revision + 1)
  }

  if (event.type === 'START_SYNC_ACTIVATION') {
    return createSyncActivationState(event.step ?? 'threshold', state.revision + 1)
  }

  if (event.type === 'START_DEACTIVATED_SYNC_REACTIVATION') {
    return createSyncActivationState('reactivate-from-deactivated', state.revision + 1)
  }

  if (event.type === 'CANCEL') {
    return { flow: 'none', revision: state.revision + 1 }
  }

  if (event.type === 'COMPLETE') {
    return { flow: 'none', revision: state.revision + 1 }
  }

  if (state.flow === 'auth-login') {
    const nextStep = reduceAuthLoginStep(state.step, event)
    if (nextStep === state.step) return state
    if (nextStep === 'done') {
      return createAuthLoginState('done', state.revision + 1)
    }
    return createAuthLoginState(nextStep, state.revision + 1)
  }

  if (state.flow === 'subscription-control') {
    const nextStep = reduceSubscriptionControlStep(state.step, event)
    if (nextStep === state.step) return state
    return createSubscriptionControlState(nextStep, state.revision + 1)
  }

  if (state.flow === 'sync-activation') {
    const nextStep = reduceSyncActivationStep(state.step, event)
    if (nextStep === state.step) return state
    return createSyncActivationState(nextStep, state.revision + 1)
  }

  return state
}

export function authLoginStepToModalMode(step: AuthLoginStep): AuthModalMode | undefined {
  if (step === 'done') return undefined
  return step
}

export function isAuthLoginModalMode(mode: AuthModalMode | undefined): boolean {
  return mode === 'email'
    || mode === 'checking-human'
    || mode === 'checking-human-failed'
    || mode === 'email-sent'
    || mode === 'username'
}

export function subscriptionControlStepToModalMode(step: SubscriptionControlStep): AuthModalMode {
  if (step === 'cancel-confirm') return 'sync-cancel-subscription'
  if (step === 'cancel-success') return 'sync-cancelled'
  if (step === 'reactivate-confirm') return 'sync-reactivate-subscription'
  return 'sync-reactivated'
}

export function isSubscriptionControlModalMode(mode: AuthModalMode | undefined): boolean {
  return mode === 'sync-cancel-subscription'
    || mode === 'sync-cancelled'
    || mode === 'sync-reactivate-subscription'
    || mode === 'sync-reactivated'
}

export function syncActivationStepToModalMode(step: SyncActivationStep): AuthModalMode | undefined {
  if (step === 'threshold') return 'sync-subscription-threshold'
  if (step === 'options') return 'checkout-start'
  if (step === 'activating') return 'checkout-activating'
  if (step === 'login-ready') return 'checkout-landing'
  if (step === 'landing') return 'checkout-landing'
  if (step === 'timeout') return 'checkout-timeout'
  if (step === 'reactivate-from-deactivated') return 'sync-reactivate-from-deactivated'
  if (step === 'username') return 'username'
  return undefined
}

export function isSyncActivationModalMode(mode: AuthModalMode | undefined): boolean {
  return mode === 'sync-subscription-threshold'
    || mode === 'checkout-start'
    || mode === 'checkout-activating'
    || mode === 'checkout-landing'
    || mode === 'checkout-timeout'
    || mode === 'sync-reactivate-from-deactivated'
    || mode === 'username'
}

export function isVerifiedProvisioningExitModalMode(mode: AuthModalMode | undefined): boolean {
  return mode === 'sync-subscription-threshold'
    || mode === 'checkout-start'
}

function createAuthLoginState(step: AuthLoginStep, revision: number): ModalFlowState {
  return {
    flow: 'auth-login',
    step,
    owner: 'auth-login',
    revision
  }
}

function createSyncActivationState(step: SyncActivationStep, revision: number): ModalFlowState {
  return {
    flow: 'sync-activation',
    step,
    owner: 'sync-activation',
    revision
  }
}

function createSubscriptionControlState(step: SubscriptionControlStep, revision: number): ModalFlowState {
  return {
    flow: 'subscription-control',
    step,
    owner: 'subscription-control',
    revision
  }
}

function reduceAuthLoginStep(step: AuthLoginStep, event: ModalFlowEvent): AuthLoginStep {
  if (event.type === 'AUTH_CHANGE_EMAIL') return 'email'
  if (event.type === 'AUTH_HUMAN_CHECK_FAILED') return 'checking-human-failed'
  if (event.type === 'AUTH_OTP_SENT') return 'email-sent'
  if (event.type === 'AUTH_PROFILE_MISSING_USERNAME') return 'username'
  if (event.type === 'AUTH_USERNAME_SAVED') return 'done'
  if (event.type === 'AUTH_OTP_VERIFIED') return 'done'

  if (step === 'email' && event.type === 'AUTH_EMAIL_SUBMITTED') {
    return 'checking-human'
  }
  if (step === 'email' && event.type === 'AUTH_HUMAN_CHECK_REQUIRED') {
    return 'checking-human'
  }
  if (step === 'checking-human-failed' && event.type === 'AUTH_HUMAN_CHECK_REQUIRED') {
    return 'checking-human'
  }

  return step
}

function reduceSubscriptionControlStep(
  step: SubscriptionControlStep,
  event: ModalFlowEvent
): SubscriptionControlStep {
  if (step === 'cancel-confirm' && event.type === 'SUBSCRIPTION_CANCEL_OK') {
    return 'cancel-success'
  }
  if (step === 'reactivate-confirm' && event.type === 'SUBSCRIPTION_REACTIVATE_OK') {
    return 'reactivate-success'
  }
  return step
}

function reduceSyncActivationStep(
  step: SyncActivationStep,
  event: ModalFlowEvent
): SyncActivationStep {
  if (event.type === 'SYNC_ACTIVATION_OPTIONS') return 'options'
  if (event.type === 'SYNC_ACTIVATION_CHECKOUT_RETURN') return 'activating'
  if (event.type === 'SYNC_ACTIVATION_READY_FOR_FRESH_LOGIN') return 'login-ready'
  if (event.type === 'SYNC_ACTIVATION_VERIFICATION_FAILED') return 'threshold'
  if (event.type === 'SYNC_ACTIVATION_PROFILE_ACTIVE') return 'landing'
  if (event.type === 'SYNC_ACTIVATION_TIMEOUT') return 'timeout'
  if (event.type === 'SYNC_ACTIVATION_RETRY') return 'activating'
  if (event.type === 'SYNC_ACTIVATION_MISSING_USERNAME') return 'username'
  if (event.type === 'SYNC_ACTIVATION_USERNAME_SAVED') return 'done'
  return step
}
